import { useEffect } from 'react';

import { KeyGrid } from '../../components/KeyGrid';
import { PredictionRow } from '../../components/PredictionRow';
import { useComposerStore } from '../../core/composer/useComposer';
import { usePrediction } from '../../core/prediction/usePrediction';
import { expandAbbrev } from '../../core/prediction/abbrev';
import { speakKeyEcho } from '../../core/speech/keyEcho';
import { useProfileStore } from '../../core/profile/ProfileStore';

/**
 * The primary surface: type and be heard.
 *
 * Presentation plus wiring only — no storage access and no provider knowledge (the layering rule, CON-10).
 * The output bar and the bottom bar belong to `AppFrame`, so this owns exactly the reserved
 * prediction row and the keyboard.
 */
export function Sprechen(): React.JSX.Element {
  const composer = useComposerStore();
  const settings = useProfileStore((s) => s.settings);
  const startPrediction = usePrediction((s) => s.start);
  const requestPrediction = usePrediction((s) => s.request);
  const clearPrediction = usePrediction((s) => s.clear);

  // Starts the worker only when prediction is enabled — off by default, so nothing is fetched or
  // parsed until stage 2 makes the ranking honest enough to switch on.
  useEffect(() => startPrediction(), [startPrediction, settings.predictionEnabled]);

  /**
   * Ask for candidates from the **composer's** state rather than from the keypress that changed it.
   *
   * It used to be asked at the end of `handleKey`, which covered typing and nothing else (C67). Speaking cleared
   * the composer and left the row showing completions for the sentence just spoken; so did `Löschen` and
   * `Wort löschen`. Tapping one of those stale candidates would have started a new sentence with the tail of the
   * old one — and a row of plausible German words gives nobody a reason to suspect it.
   *
   * Driving it from the text means every route that changes the composer is covered by construction, including
   * the ones that do not exist yet — and one of them has since arrived. With `keepTextAfterSpeak` on, speaking
   * no longer clears the composer at all, it appends a space; this effect needed no change, because the space
   * makes `wordBeforeCaret()` empty and the row clears exactly as it does mid-sentence.
   */
  useEffect(() => {
    const prefix = composer.wordBeforeCaret();
    if (prefix === '') {
      clearPrediction();
      return;
    }
    requestPrediction(prefix, previousWord(composer.text()));
    // `text` is the dependency because it is what makes the previous answer wrong.
  }, [composer, composer.state.text, clearPrediction, requestPrediction]);

  const handleKey = (key: string): void => {
    // Echo first and fire-and-forget: it must never sit between the keypress and the letter
    // appearing (NFR2), and it deliberately bypasses SpeechService so 33 letters a minute do not
    // land in history, diagnostics or Phase 2's personal n-grams.
    if (settings.keyEchoEnabled) void speakKeyEcho(key);

    // The space finishes a word, which is the moment its capital can be settled — a noun the person
    // spelled out in full has no other way to get one, because there is no shift key. Read from the
    // store rather than from a render-time value: the answer may have arrived in this same tick.
    if (key === ' ') {
      const { prefix, canonical } = usePrediction.getState().casing;
      if (canonical !== null && prefix === composer.wordBeforeCaret()) composer.recaseWord(canonical);
    }

    composer.insertTyped(key);

    // Abbreviation expansion fires on the word boundary the space just created. Deterministic,
    // no lexicon, no visual search cost — which for an unknown visual impairment may make it
    // outperform prediction outright (§5.3b).
    if (settings.abbrevEnabled && key === ' ') void composer.tryExpandAbbrev(expandAbbrev);

  };

  return (
    <>
      <PredictionRow
        onAccept={(candidate) => {
          composer.acceptCompletion(candidate.word);
          // The accepted word is now the context for the next one, so ask again straight away — the
          // row should already be showing what usually follows before the person looks up.
          requestPrediction('', candidate.word);
        }}
      />
      <KeyGrid onKey={handleKey} />
    </>
  );
}

/**
 * The completed word before the one being typed, for bigram context.
 *
 * Null when the person is on their first word — there is nothing to condition on, and guessing from
 * nothing would fill four expensive slots with the commonest words in German.
 */
function previousWord(text: string): string | null {
  const words = text.trim().split(/\s+/u).filter(Boolean);
  // The last entry is the word currently being typed, so the one before it is the context.
  return words.length >= 2 ? (words[words.length - 2] ?? null) : null;
}
